import React from 'react';
import axios from "axios";
import Cookies from "universal-cookie";
import {jwtDecode} from 'jwt-decode';

import Property from "./Property";

class Utils extends React.Component {
  static cookies = new Cookies();
  static refreshPromise = null;
  static interceptorInstalled = false;

  static showMessage(componentObject, error) {
    let message = error.message;
    if (typeof error.response !== 'undefined' && typeof error.response.data !== 'undefined') {
      message = error.response.status + ":" + (error.response.data.message ?? error.response.data.msg);
    }
    if (componentObject) {
      componentObject.setState({message: message});
      componentObject.state['message'] = message;
    }
  }

  // Global auth: transparently attach a fresh access token to every request, refreshing
  // via the refresh token when expired. Anonymous requests go out without a header.
  static setupAxiosInterceptors() {
    if (this.interceptorInstalled) {
      return;
    }
    this.interceptorInstalled = true;
    axios.interceptors.request.use(async (config) => {
      // The refresh call authenticates itself; skip to avoid recursion.
      if (config.url && config.url.includes("/api/refresh")) {
        return config;
      }
      const token = await Utils.ensureFreshAccessToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = "Bearer " + token;
      }
      return config;
    });
  }

  static generateAuthorizationHeaderValue() {
    return "Bearer " + this.getAccessToken();
  }

  // Returns a valid access token, refreshing it if expired. Anonymous users get null.
  // Concurrent callers share a single in-flight refresh to avoid a refresh stampede.
  static ensureFreshAccessToken(componentObject) {
    let accessToken = this.getAccessToken();
    if (!accessToken) {
      return Promise.resolve(null);
    }
    if (!this.isTokenExpired(accessToken)) {
      return Promise.resolve(accessToken);
    }
    let refreshToken = this.getRefreshToken();
    if (!refreshToken || this.isTokenExpired(refreshToken)) {
      this.setAccessToken(null);
      this.setRefreshToken(null);
      return Promise.resolve(null);
    }
    if (!this.refreshPromise) {
      this.refreshPromise = Promise.resolve(this.refreshAccessToken(componentObject))
        .finally(() => {
          this.refreshPromise = null;
        });
    }
    return this.refreshPromise;
  }

  static getCookie(name) {
    return this.cookies.get(name);
  }

  static setCookie(name, value) {
    if (!value) {
      this.cookies.remove(name);
      return null;
    }
    this.cookies.set(name, value);
    return value;
  }

  static getAccessToken() {
    return this.getCookie("access-token");
  }

  static setAccessToken(accessToken) {
    return this.setCookie("access-token", accessToken);
  }

  static getRefreshToken() {
    return this.getCookie("refresh-token");
  }

  static setRefreshToken(refreshToken) {
    return this.setCookie("refresh-token", refreshToken);
  }

  static isLogged() {
    //todo improve method, sometimes work incorrectly, maybe send a request (no!) to understand if it is still valid?
    let token = this.getAccessToken();
    return !!token && !this.isTokenExpired(token);
  }

  static getTokenExpirationDate(token) {
    if (typeof token === "string") {
      token = jwtDecode(token);
    }
    return token ? new Date((token.exp) * 1000) : null;
  }

  static deleteToken(token) {
    if (!token || !token.id) {
      return Promise.reject(new Error('No token id provided'));
    }

    const url = `${Property.BASE_API_URL}/api/tokens/${token.id}`;

    return axios.delete(url, {
      headers: {
        'Authorization': this.generateAuthorizationHeaderValue(),
        'Content-Type': 'application/json',
        'Access-Control-Allow-Methods': 'DELETE'
      }
    }).then(() => {
      // Remove from storage if it matches our current tokens
      if (token.type === 'access_token') {
        this.setAccessToken(null);
      } else if (token.type === 'refresh_token') {
        this.setRefreshToken(null);
      }
      return token;
    });
  }

  static isTokenExpired(token) {
    let currentTime = new Date().getTime();
    let tokenTime = this.getTokenExpirationDate(token).getTime();
    return tokenTime < currentTime; //if token time before current then token is expired
  }

  //todo: introduce a decorator for post or get methods and call this method inside if needed
  static refreshAccessToken(componentObject) {
    let site = Property.BASE_API_URL;
    let updateTokenUrl = site + "/api/refresh";
    let refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      if (componentObject) {
        this.showMessage(componentObject, 'No refresh token exist.');
      }
      return null;
    }
    let updateConfig = {
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Methods': 'POST',
        'Access-Control-Allow-Origin': site,
        'Authorization': this.generateAuthorizationHeaderValue()
      }
    };

    return axios.post(updateTokenUrl, null, updateConfig)
      .then((response) => {
        console.log("update token response: ", response);
        Utils.setAccessToken(response.data.access_token);
        return response.data.access_token;
      }).catch((error) => {
        console.log(error);
        if (error.response == null) {
          console.log('404 ', error);
        } else if (error.response.status === 401) {
          console.log('401 ', error);
        }
        this.showMessage(componentObject, error);
      });
  }
}

export default Utils